import type { IAnnotation, IArea, IDrawing, IGridgioWork } from '@datatype/gridgio/type';
import { createModifiedDrawingPdf } from '@util/gridgio-export';
import { saveNativeFile } from '@util/native-file';

const FORMAT = 'gridgio-work';
const SCHEMA_VERSION = 1;
const MANIFEST_PATH = 'manifest.json';
const ANNOTATION_PATH = 'annotation.json';
const MODIFIED_SOURCE_PATH = 'source/modified.pdf';
const WORK_MIME_TYPE = 'application/vnd.gridgio.work+zip';

interface IFileSystemWritableFileStream {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

interface IFileSystemFileHandle {
  createWritable: () => Promise<IFileSystemWritableFileStream>;
}

type TSaveFilePicker = (options: {
  suggestedName: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<IFileSystemFileHandle>;

interface IWorkManifest {
  format: typeof FORMAT;
  schemaVersion: typeof SCHEMA_VERSION;
  createdAt: string;
  source: {
    path: string;
    modifiedPath: string;
    filename: string;
    type: IDrawing['type'];
    contentType: string;
    totalSheet: number;
  };
  configuration: {
    rotation: number;
    workArea: IArea | null;
    keyPlanArea: IArea | null;
  };
  annotation: {
    path: string;
  };
}

const getSourcePath = (drawing: IDrawing) => {
  const extension = drawing.name.match(/\.[^.]+$/)?.[0] ?? (drawing.type === 'pdf' ? '.pdf' : '');
  return `source/plan${extension.toLowerCase()}`;
};

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isPoint = (value: unknown) => {
  if (!value || typeof value !== 'object') return false;
  const { x, y } = value as Record<string, unknown>;
  return isNumber(x) && isNumber(y) && x >= 0 && x <= 1 && y >= 0 && y <= 1;
};

const isArea = (value: unknown): value is IArea | null => {
  if (value === null) return true;
  if (!value || typeof value !== 'object') return false;
  const area = value as Record<string, unknown>;
  return (
    Array.isArray(area.point) &&
    area.point.length === 4 &&
    area.point.every((position) => {
      return isPoint(position);
    })
  );
};

const isStrokeContainer = (value: unknown, width = 1, height = 1) => {
  if (!value || typeof value !== 'object') return false;
  const container = value as Record<string, unknown>;
  return (
    Array.isArray(container.stroke) &&
    container.stroke.every((stroke) => {
      if (!stroke || typeof stroke !== 'object') return false;
      const { point } = stroke as Record<string, unknown>;
      return (
        Array.isArray(point) &&
        point.every((position) => {
          if (!position || typeof position !== 'object') return false;
          const { x, y } = position as Record<string, unknown>;
          return isNumber(x) && isNumber(y) && x >= 0 && x <= width && y >= 0 && y <= height;
        })
      );
    })
  );
};

const isAnnotationProperty = (value: unknown) => {
  if (value === undefined) return true;
  if (!Array.isArray(value)) return false;

  const type = new Set<string>();
  return value.every((item) => {
    if (!isHandwriting(item)) return false;
    const property = item as Record<string, unknown>;
    if (typeof property.type !== 'string' || type.has(property.type)) return false;
    type.add(property.type);

    const hasValidTypeAndUnit = ['width', 'length'].includes(property.type) && property.unit === 'mm';
    return hasValidTypeAndUnit && (property.value === undefined || (isNumber(property.value) && property.value >= 0));
  });
};

const isMemoCanvas = (value: unknown) => {
  if (!value || typeof value !== 'object') return false;
  const { width, height } = value as Record<string, unknown>;
  return isNumber(width) && width > 0 && isNumber(height) && height > 0;
};

const isHandwriting = (value: unknown) => {
  if (!value || typeof value !== 'object') return false;
  const canvas = (value as Record<string, unknown>).canvas;
  if (!isMemoCanvas(canvas)) return false;
  const { width, height } = canvas as { width: number; height: number };
  return isStrokeContainer(value, width, height);
};

const isAnnotation = (value: unknown): value is IAnnotation => {
  if (!value || typeof value !== 'object') return false;
  const annotation = value as Record<string, unknown>;
  return (
    typeof annotation.id === 'string' &&
    typeof annotation.issueId === 'string' &&
    isStrokeContainer(annotation.mark) &&
    !('symbol' in (annotation.mark as Record<string, unknown>)) &&
    Array.isArray(annotation.memo) &&
    annotation.memo.every(
      (memo) =>
        Boolean(memo) &&
        typeof memo === 'object' &&
        typeof (memo as Record<string, unknown>).id === 'string' &&
        ((memo as Record<string, unknown>).updatedAt === undefined ||
          typeof (memo as Record<string, unknown>).updatedAt === 'string') &&
        isHandwriting(memo),
    ) &&
    isAnnotationProperty(annotation.property)
  );
};

const parseAnnotation = (value: unknown, totalSheet: number): Record<string, IAnnotation[]> => {
  if (
    !value ||
    typeof value !== 'object' ||
    !Object.entries(value).every(
      ([sheetKey, annotation]) =>
        /^sheet[1-9]\d*$/.test(sheetKey) &&
        Number(sheetKey.replace('sheet', '')) <= totalSheet &&
        Array.isArray(annotation) &&
        annotation.every(isAnnotation),
    )
  ) {
    throw new Error('프로젝트의 Annotation 정보가 올바르지 않습니다.');
  }
  return value as Record<string, IAnnotation[]>;
};

const parseManifest = (value: unknown): IWorkManifest => {
  if (!value || typeof value !== 'object') throw new Error('프로젝트 정보가 올바르지 않습니다.');
  const manifest = value as IWorkManifest;
  if (manifest.format !== FORMAT || manifest.schemaVersion !== SCHEMA_VERSION) {
    throw new Error('지원하지 않는 GridgIO Work 형식입니다.');
  }
  if (
    !manifest.source ||
    typeof manifest.source.path !== 'string' ||
    typeof manifest.source.modifiedPath !== 'string' ||
    typeof manifest.source.filename !== 'string' ||
    !['pdf', 'image'].includes(manifest.source.type) ||
    typeof manifest.source.contentType !== 'string' ||
    !Number.isInteger(manifest.source.totalSheet) ||
    manifest.source.totalSheet < 1
  ) {
    throw new Error('프로젝트의 도면 정보가 올바르지 않습니다.');
  }
  if (
    !manifest.configuration ||
    ![0, 90, 180, 270].includes(manifest.configuration.rotation) ||
    !isArea(manifest.configuration.workArea) ||
    !isArea(manifest.configuration.keyPlanArea) ||
    !manifest.annotation ||
    typeof manifest.annotation.path !== 'string'
  ) {
    throw new Error('프로젝트의 Workspace 정보가 올바르지 않습니다.');
  }
  return manifest;
};

export const serializeGridgioWork = async (work: IGridgioWork): Promise<Blob> => {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const sourcePath = getSourcePath(work.drawing);
  const manifest: IWorkManifest = {
    format: FORMAT,
    schemaVersion: SCHEMA_VERSION,
    createdAt: new Date().toISOString(),
    source: {
      path: sourcePath,
      modifiedPath: MODIFIED_SOURCE_PATH,
      filename: work.drawing.name,
      type: work.drawing.type,
      contentType: work.drawing.file.type,
      totalSheet: work.totalPage,
    },
    configuration: {
      rotation: work.rotation,
      workArea: work.workArea,
      keyPlanArea: work.keyPlanArea,
    },
    annotation: { path: ANNOTATION_PATH },
  };

  zip.file(MANIFEST_PATH, JSON.stringify(manifest, null, 2));
  zip.file(ANNOTATION_PATH, JSON.stringify(work.annotation, null, 2));
  zip.file(sourcePath, work.drawing.file);
  zip.file(
    MODIFIED_SOURCE_PATH,
    await createModifiedDrawingPdf({
      drawing: work.drawing,
      rotation: work.rotation,
      workArea: work.workArea,
      annotation: work.annotation,
    }),
  );
  return zip.generateAsync({ type: 'blob', mimeType: WORK_MIME_TYPE, compression: 'DEFLATE' });
};

export const deserializeGridgioWork = async (file: File): Promise<IGridgioWork> => {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(file);
  const manifestEntry = zip.file(MANIFEST_PATH);
  if (!manifestEntry) throw new Error('manifest.json이 없습니다.');
  const manifest = parseManifest(JSON.parse(await manifestEntry.async('string')));
  const annotationEntry = zip.file(manifest.annotation.path);
  if (!annotationEntry) throw new Error('annotation.json이 없습니다.');
  const annotation = parseAnnotation(JSON.parse(await annotationEntry.async('string')), manifest.source.totalSheet);
  const drawingEntry = zip.file(manifest.source.path);
  if (!drawingEntry) throw new Error('프로젝트의 원본 도면이 없습니다.');
  if (!zip.file(manifest.source.modifiedPath)) throw new Error('프로젝트의 수정 도면이 없습니다.');
  const drawingBlob = await drawingEntry.async('blob');
  const drawingFile = new File([drawingBlob], manifest.source.filename, { type: manifest.source.contentType });

  return {
    drawing: {
      file: drawingFile,
      url: URL.createObjectURL(drawingFile),
      name: manifest.source.filename,
      type: manifest.source.type,
    },
    rotation: manifest.configuration.rotation,
    workArea: manifest.configuration.workArea,
    keyPlanArea: manifest.configuration.keyPlanArea,
    annotation,
    totalPage: manifest.source.totalSheet,
  };
};

export const getGridgioWorkFileName = (drawingName: string) => `${drawingName.replace(/\.[^.]+$/, '')}.giw`;

const downloadGridgioWork = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const saveGridgioWork = async (work: IGridgioWork) => {
  const fileName = getGridgioWorkFileName(work.drawing.name);
  if (window.ReactNativeWebView) {
    const blob = await serializeGridgioWork(work);
    return saveNativeFile(blob, fileName);
  }
  const saveFilePicker = (window as typeof window & { showSaveFilePicker?: TSaveFilePicker }).showSaveFilePicker;
  const fileHandle = saveFilePicker
    ? await saveFilePicker.call(window, {
        suggestedName: fileName,
        types: [{ description: 'GridgIO Work', accept: { [WORK_MIME_TYPE]: ['.giw'] } }],
      })
    : null;
  const blob = await serializeGridgioWork(work);

  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  downloadGridgioWork(blob, fileName);
};
